import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, message } from 'antd';
import {
  FileExcelOutlined,
  DownloadOutlined,
  AuditOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  LoginOutlined,
  SettingOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  WarningOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Layout from '../../components/Layout';
import AllAuditTab from './AllAuditTab';
import AdminAuditTab from './AdminAuditTab';
import CustomerAuditTab from './CustomerAuditTab';
import LoginAuditTab from './LoginAuditTab';
import SystemChangesTab from './SystemChangesTab';
import { getAuditTrailStats, getAuditTrailLogs } from '../../services/auditTrailService';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import DashboardStatCard from '../../components/shared/DashboardStatCard';
import './AuditTrail.css';

const AuditTrail = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    adminActions: 0,
    customerActions: 0,
    failedLogins: 0,
    systemChanges: 0,
    loginAttempts: 0,
    successRate: 0,
    recentEvents24h: 0,
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getAuditTrailStats();
        if (res?.success) {
          setStats(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch audit trail stats:', err);
      }
    };
    fetchStats();
  }, []);

  // ── Export Handlers ────────────────────────────────────────────────────
  const getExportData = async () => {
    setExporting(true);
    try {
      // Fetch a large page of all events for export
      const res = await getAuditTrailLogs({ page: 0, size: 1000 });
      if (!res?.success || !res.data?.content?.length) {
        message.warning('No records to export');
        return null;
      }
      return res.data.content.map((log) => ({
        'ID': log.id,
        'Event Type': log.eventType || '',
        'Entity Type': log.entityType || '',
        'Entity ID': log.entityId || '',
        'User ID': log.userId || '',
        'Action': log.action || '',
        'Description': log.description || '',
        'Old Values': log.oldValues || '',
        'New Values': log.newValues || '',
        'IP Address': log.ipAddress || '',
        'User Agent': log.userAgent || '',
        'Session ID': log.sessionId || '',
        'Status': log.status || '',
        'Error Message': log.errorMessage || '',
        'Timestamp': log.timestamp || '',
      }));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
    message.success('CSV exported successfully');
  };

  const handleExportExcel = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Trail');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `audit_trail_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success('Excel file exported successfully');
  };

  const exportMenuItems = [
    {
      key: 'csv',
      label: 'Export as CSV',
      icon: <DownloadOutlined />,
      onClick: handleExportCSV,
    },
    {
      key: 'excel',
      label: 'Export as Excel',
      icon: <FileExcelOutlined />,
      onClick: handleExportExcel,
    },
  ];

  // ── Tabs Configuration ────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'all',
      label: (
        <span className="audit-tab-badge">
          <AuditOutlined className="tab-icon" />
          All Activity
        </span>
      ),
      children: <AllAuditTab />,
    },
    {
      key: 'admin',
      label: (
        <span className="audit-tab-badge">
          <SafetyCertificateOutlined className="tab-icon" />
          Admin Activity
        </span>
      ),
      children: <AdminAuditTab />,
    },
    {
      key: 'customer',
      label: (
        <span className="audit-tab-badge">
          <UserOutlined className="tab-icon" />
          Customer Activity
        </span>
      ),
      children: <CustomerAuditTab />,
    },
    {
      key: 'login',
      label: (
        <span className="audit-tab-badge">
          <LoginOutlined className="tab-icon" />
          Login Activity
        </span>
      ),
      children: <LoginAuditTab />,
    },
    {
      key: 'system',
      label: (
        <span className="audit-tab-badge">
          <SettingOutlined className="tab-icon" />
          System Changes
        </span>
      ),
      children: <SystemChangesTab />,
    },
  ];

  // ── Today's date range display ─────────────────────────────────────────
  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Layout>
      <div className="audit-trail-wrapper" style={{ padding: '0 24px' }}>
        {/* ── Hero Header ───────────────────────────────────────────── */}
        <PageHeadingBanner
          icon={<AuditOutlined />}
          title="Audit Trail Management"
          subtitle="Complete audit trail of system activities, configuration changes, and user behavior"
          actions={
            <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
              <Button
                icon={<DownloadOutlined />}
                className="phb-btn-primary"
                size="large"
                loading={exporting}
              >
                Export Logs
              </Button>
            </Dropdown>
          }
        />

        {/* ── Dashboard Stats ───────────────────────────────────────── */}
        <div className="audit-dashboard-section">
          <div className="ds-stats-grid cols-5">
            <DashboardStatCard
              icon={<AuditOutlined />}
              value={stats.total || 0}
              label="Total Events"
              iconBg="#fff3eb"
              iconColor="#ff6700"
            />
            <DashboardStatCard
              icon={<LoginOutlined />}
              value={stats.loginAttempts || 0}
              label="Login Events"
              iconBg="#f9f0ff"
              iconColor="#722ed1"
            />
            <DashboardStatCard
              icon={<CheckCircleOutlined />}
              value={stats.successRate ? `${stats.successRate}%` : '—'}
              label="Success Rate"
              iconBg="#e6f7ff"
              iconColor="#1890ff"
            />
            <DashboardStatCard
              icon={<WarningOutlined />}
              value={stats.failedLogins || 0}
              label="Failed Events"
              iconBg="#fff2f0"
              iconColor="#ff4d4f"
            />
            <DashboardStatCard
              icon={<SyncOutlined />}
              value={stats.systemChanges || 0}
              label="System Changes"
              iconBg="#e6fffb"
              iconColor="#13c2c2"
            />
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="audit-trail-tabs"
        />
      </div>
    </Layout>
  );
};

export default AuditTrail;
